import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DecisionPersonRef, MAX_DECISION_DECIDERS } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogApply } from '../../../components/ui/dialog';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { taskFormApi, taskFormKeys } from '../../tasks/api';
import { decisionSheetApi, decisionSheetKeys } from '../api';
import { isLiveShare, ShareLinksPanel } from './share-dialog';

export type AnswerersTab = 'members' | 'link';

/**
 * "Chọn người trả lời" — hai đường trả lời của một sổ trong một hộp
 * (người dùng 2026-10-07: đổi tên nút "Người chốt" và gộp nút "Chia sẻ" vào):
 *
 * - **Thành viên dự án** — người chốt (plan §4.1): gửi **phần chênh**
 *   (`add` / `remove`), mỗi lần lưu là một event `deciders_changed`.
 * - **Người ngoài qua link** — link công khai (plan §11.3), chỉ khi `canShare`.
 *
 * Sổ mở được khi có câu hỏi và có **ít nhất một** trong hai (server kiểm).
 * Mở từ "Mở sổ" (`onOpen`): lưu thành viên xong thì mở luôn; đã có link "Trả
 * lời" còn hiệu lực thì hiện nút "Mở sổ ngay".
 */
export function DecidersDialog({
  projectId,
  sheetId,
  current,
  canShare,
  initialTab = 'members',
  onClose,
  onOpen,
  intro,
}: {
  readonly projectId: string;
  readonly sheetId: string;
  readonly current: readonly DecisionPersonRef[];
  readonly canShare: boolean;
  readonly initialTab?: AnswerersTab;
  readonly onClose: () => void;
  /** Có khi hộp mở ra từ "Mở sổ": sau khi có người trả lời thì mở sổ luôn. */
  readonly onOpen?: (() => void) | undefined;
  /** Dòng nhắc riêng ở đầu hộp — vì sao hộp này hiện ra. */
  readonly intro?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<AnswerersTab>(canShare ? initialTab : 'members');
  const initial = new Set(current.map((person) => person.id));
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initial));

  const members = useQuery({
    queryKey: taskFormKeys.members(projectId),
    queryFn: () => taskFormApi.members(projectId),
  });
  // Cùng khoá cache với panel link — panel tạo / thu hồi là ở đây biết ngay.
  const links = useQuery({
    queryKey: decisionSheetKeys.shares(sheetId),
    queryFn: () => decisionSheetApi.shares(sheetId),
    enabled: canShare && onOpen !== undefined,
  });
  const hasAnswerLink = (links.data ?? []).some(
    (link) => link.mode === 'answer' && isLiveShare(link),
  );

  const add = [...picked].filter((id) => !initial.has(id));
  const remove = [...initial].filter((id) => !picked.has(id));

  const save = useMutation({
    mutationFn: () => decisionSheetApi.changeDeciders(sheetId, { add, remove }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', sheetId] });
      void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(projectId) });
      toast.success(t('decisionSheet.decidersSaved'));
      onClose();
      if (picked.size > 0) onOpen?.();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const toggle = (userId: string): void =>
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const overLimit = picked.size > MAX_DECISION_DECIDERS;
  // Người chốt cũ đã rời dự án vẫn phải bỏ chọn được — không thì không ai gỡ nổi họ.
  const memberIds = new Set(members.data?.map((member) => member.userId));
  const departed = current.filter((person) => !memberIds.has(person.id));

  return (
    <Dialog label={t('decisionSheet.answerers.title')} onClose={onClose} fixedHeight>
      {tab === 'members' ? (
        <DialogApply
          label={t('decisionSheet.decidersSave')}
          disabled={add.length + remove.length === 0 || overLimit}
          pending={save.isPending}
          onClick={() => save.mutate()}
        />
      ) : null}
      <h2 className="text-lg font-semibold">{t('decisionSheet.answerers.title')}</h2>
      {intro ? (
        <p
          className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
          data-testid="deciders-intro"
        >
          {intro}
        </p>
      ) : null}
      {onOpen && hasAnswerLink ? (
        <Button
          className="self-start"
          data-testid="answerers-open-now"
          onClick={() => {
            onClose();
            onOpen();
          }}
        >
          {t('decisionSheet.answerers.openNow')}
        </Button>
      ) : null}

      {canShare ? (
        <GlassSegmented<AnswerersTab>
          size="sm"
          ariaLabel={t('decisionSheet.answerers.title')}
          value={tab}
          onChange={setTab}
          options={(['members', 'link'] as const).map((value) => ({
            value,
            label: t(`decisionSheet.answerers.tabs.${value}`),
          }))}
        />
      ) : null}

      {tab === 'link' ? (
        <ShareLinksPanel sheetId={sheetId} />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('decisionSheet.decidersHint')}</p>
          {members.isPending ? (
            <Skeleton className="h-32" />
          ) : members.isError ? (
            <ErrorState
              message={errorMessage(members.error, t)}
              onRetry={() => void members.refetch()}
            />
          ) : members.data.length === 0 && departed.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('decisionSheet.decidersEmpty')}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {[
                ...members.data.map((member) => ({
                  id: member.userId,
                  name: member.displayName,
                  hint: member.roleName,
                })),
                ...departed.map((person) => ({ id: person.id, name: person.name, hint: '' })),
              ].map((person) => (
                <li key={person.id}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-DEFAULT px-2 py-1.5 text-sm hover:bg-muted/50">
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 accent-primary"
                      checked={picked.has(person.id)}
                      onChange={() => toggle(person.id)}
                    />
                    <span className="min-w-0 flex-1 truncate">{person.name}</span>
                    {person.hint ? (
                      <span className="shrink-0 text-xs text-muted-foreground">{person.hint}</span>
                    ) : null}
                  </label>
                </li>
              ))}
            </ul>
          )}

          {overLimit ? (
            <p role="alert" className="text-xs text-destructive">
              {t('decisionSheet.decidersLimit', { max: MAX_DECISION_DECIDERS })}
            </p>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
